import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { HoldingForm, HoldingFormValues } from "../../components/holding-form";
import { Modal } from "../../components/modal";
import { formValuesToResidencyName } from "../../components/residency-name-input";
import { useAdditionalFields } from "../../hooks/addition-fields";
import { parseFormValuesToAdditionalFields } from "../../services/additional-fields";
import { createHolding } from "../../services/holding";
import { navigateBack } from "../../utils/navigate-back";

export function HoldingCreate() {
  const navigate = useNavigate();
  const { data: additionalFields } = useAdditionalFields("holding");
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [formValues, setFormValues] = useState<HoldingFormValues>({
    apartmentId: "",
    fromDate: "",
    toDate: "",
    useCustomName: false,
    customName: "",
    owners: [
      {
        personId: "",
        primary: true,
        share: 100,
      },
    ],
    additionalFields: additionalFields.reduce(
      (acc, field) => ({
        ...acc,
        [field.name]: "",
      }),
      {},
    ),
  });

  const closeModal = () => {
    navigateBack(navigate, "/admin/register/holding");
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <HoldingForm
        hasError={hasError}
        loading={loading}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              await createHolding({
                apartmentId: formValues.apartmentId,
                owners: formValues.owners
                  .map((owner) => ({ ...owner, share: owner.share / 100 }))
                  .map((owner) => {
                    if ("personId" in owner) {
                      return {
                        personId: owner.personId,
                        primary: owner.primary,
                        share: owner.share,
                      };
                    } else if ("organizationId" in owner) {
                      return {
                        organizationId: owner.organizationId,
                        primary: owner.primary,
                        share: owner.share,
                      };
                    } else {
                      throw new Error("Invalid owner");
                    }
                  }),
                fromDate: new Date(formValues.fromDate),
                toDate: formValues.toDate
                  ? new Date(formValues.toDate)
                  : undefined,
                customName: formValuesToResidencyName(formValues) ?? undefined,
                ...(formValues.additionalFields && {
                  additionalData: parseFormValuesToAdditionalFields(
                    formValues.additionalFields,
                    additionalFields,
                  ),
                }),
              });
              await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["holdings"] }),
                queryClient.invalidateQueries({ queryKey: ["apartments"] }),
              ]);
              closeModal();
            } catch (error: unknown) {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
